import React, { useState, useRef, useMemo } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { createUserService } from '../../../../services';
import { authService } from '../../../../services';
import {
  isPasswordValid,
  validatePassword,
} from '../../../../utils/passwordvalidation';
import LoginModal from '../../../common/loginModal/LoginModal';
import './UserProfile.css';

// ── Helpers ──────────────────────────────────────────────────

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

const PROVIDER_LABEL: Record<string, string> = {
  email: 'Email e password',
  google: 'Google',
  facebook: 'Facebook',
  apple: 'Apple',
};

// ── Section wrapper ───────────────────────────────────────────

const Section: React.FC<{
  title: string;
  icon: string;
  children: React.ReactNode;
}> = ({ title, icon, children }) => (
  <section className="up-section">
    <h2 className="up-section__title">
      <i className={`bi ${icon}`} aria-hidden="true" />
      {title}
    </h2>
    <div className="up-section__body">{children}</div>
  </section>
);

// ── Main component ────────────────────────────────────────────

const UserProfile: React.FC = () => {
  const { user, logout, updateUser } = useAuth();
  const userService = useMemo(
    () => (user ? createUserService(user.id) : null),
    [user?.id] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── dati personali ──
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [saveError, setSaveError] = useState('');

  // ── cambio password ──
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdOk, setPwdOk] = useState(false);
  const [pwdError, setPwdError] = useState('');

  // ── reinvio email ──
  const [resendLoading, setResendLoading] = useState(false);
  const [resendOk, setResendOk] = useState(false);
  const [resendError, setResendError] = useState('');

  // ── delete account ──
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // ── avatar ──
  const fileRef = useRef<HTMLInputElement>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(
    user?.avatarUrl ?? null
  );
  const [avatarError, setAvatarError] = useState('');

  // ── delete account ──
  const [deleteError, setDeleteError] = useState('');

  // ── modal completamento registrazione free ──
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  const initials =
    `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase();

  // ── handlers ────────────────────────────────────────────────

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarError('');
    const previousAvatar = avatarPreview;
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result as string);
    reader.readAsDataURL(file);
    try {
      const { avatarUrl } = await userService!.uploadAvatar(file);
      setAvatarPreview(avatarUrl);
      updateUser({ avatarUrl });
    } catch {
      setAvatarPreview(previousAvatar);
      setAvatarError('Errore nel caricamento della foto. Riprova.');
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError('');
    if (!firstName.trim() || !lastName.trim()) {
      setSaveError('Nome e cognome sono obbligatori.');
      return;
    }
    setSaving(true);
    try {
      const updated = await userService!.updateProfile({ firstName, lastName });
      updateUser({
        firstName: updated.firstName,
        lastName: updated.lastName,
        name: updated.name,
      });
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
    } catch {
      setSaveError('Errore durante il salvataggio. Riprova.');
    } finally {
      setSaving(false);
    }
  };

  const handleResendEmail = async () => {
    setResendError('');
    setResendOk(false);
    setResendLoading(true);
    try {
      await authService.resendConfirmationEmail();
      setResendOk(true);
      setTimeout(() => setResendOk(false), 5000);
    } catch (err) {
      setResendError(
        err instanceof Error ? err.message : "Errore durante l'invio. Riprova."
      );
    } finally {
      setResendLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError('');
    if (!currentPwd) {
      setPwdError('Inserisci la password attuale.');
      return;
    }
    if (!newPwd) {
      setPwdError('Inserisci la nuova password.');
      return;
    }
    if (!isPasswordValid(validatePassword(newPwd))) {
      setPwdError(
        'La nuova password deve contenere almeno 8 caratteri, una maiuscola, un numero e un simbolo.'
      );
      return;
    }
    if (newPwd !== confirmPwd) {
      setPwdError('Le password non coincidono.');
      return;
    }
    setPwdSaving(true);
    try {
      await userService!.changePassword(currentPwd, newPwd);
      setPwdOk(true);
      setCurrentPwd('');
      setNewPwd('');
      setConfirmPwd('');
      setTimeout(() => setPwdOk(false), 3000);
    } catch (err) {
      setPwdError(
        err instanceof Error
          ? err.message
          : 'Errore durante il cambio password.'
      );
    } finally {
      setPwdSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setDeletingAccount(true);
    setDeleteError('');
    try {
      await userService!.deleteAccount();
      await logout();
    } catch {
      setDeleteError("Errore durante l'eliminazione. Riprova più tardi.");
      setDeletingAccount(false);
      setConfirmDelete(false);
    }
  };

  if (!user) return null;

  const isEmailProvider = user.authProvider === 'email';

  return (
    <div className="up-page">
      {/* ── Avatar + info utente ── */}
      <section className="up-hero">
        <div className="up-avatar-wrap">
          {avatarPreview ? (
            <img
              src={avatarPreview}
              alt={`Avatar di ${user.firstName}`}
              className="up-avatar-img"
            />
          ) : (
            <div className="up-avatar-placeholder" aria-hidden="true">
              {initials}
            </div>
          )}
          <button
            className="up-avatar-edit"
            onClick={() => fileRef.current?.click()}
            aria-label="Cambia foto profilo"
          >
            <i className="bi bi-camera-fill" />
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="up-avatar-file-input"
            onChange={handleAvatarChange}
            aria-label="Carica foto profilo"
          />
        </div>
        {avatarError && (
          <p className="up-avatar-error" role="alert">
            {avatarError}
          </p>
        )}

        <div className="up-hero__info">
          <h1 className="up-hero__name">
            {user.firstName} {user.lastName}
          </h1>
          <p className="up-hero__email">{user.email}</p>
          <div className="up-hero__badges">
            <span className="up-badge up-badge--role">
              <i className="bi bi-shield-check me-1" />
              {user.role}
            </span>
            <span
              className={`up-badge ${user.emailConfirmed ? 'up-badge--confirmed' : 'up-badge--unconfirmed'}`}
            >
              <i
                className={`bi ${user.emailConfirmed ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'} me-1`}
              />
              {user.emailConfirmed
                ? 'Email verificata'
                : 'Email non verificata'}
            </span>
            <span className="up-badge up-badge--provider">
              <i className="bi bi-key me-1" />
              {PROVIDER_LABEL[user.authProvider] ?? user.authProvider}
            </span>
          </div>
          <p className="up-hero__since">
            <i className="bi bi-calendar3 me-1" />
            Iscritto dal {formatDate(user.createdAt)}
          </p>
        </div>
      </section>

      {/* ── Email non confermata — banner ── */}
      {!user.emailConfirmed && (
        <div className="up-alert-banner" role="alert">
          <i className="bi bi-envelope-exclamation-fill" aria-hidden="true" />
          <div>
            <strong>Conferma la tua email</strong>
            <p className="mb-0 small">
              Controlla la casella di {user.email} e clicca il link di conferma.
            </p>
            {resendOk && (
              <p className="mb-0 small text-success mt-1">
                <i className="bi bi-check-circle-fill me-1" />
                Email inviata! Controlla la tua casella.
              </p>
            )}
            {resendError && (
              <p className="mb-0 small text-danger mt-1">{resendError}</p>
            )}
          </div>
          <button
            className="up-alert-banner__btn"
            onClick={handleResendEmail}
            disabled={resendLoading || resendOk}
          >
            {resendLoading ? (
              <>
                <span className="up-spinner" aria-hidden="true" /> Invio…
              </>
            ) : resendOk ? (
              'Inviata ✓'
            ) : (
              'Reinvia email'
            )}
          </button>
        </div>
      )}

      {/* ── Account free_only — banner ── */}
      {user.accountStatus === 'free_only' && (
        <div className="up-alert-banner up-alert-banner--info" role="alert">
          <i className="bi bi-stars" aria-hidden="true" />
          <div>
            <strong>Completa la registrazione</strong>
            <p className="mb-0 small">
              Aggiungi nome, cognome e password per acquistare i corsi.
            </p>
          </div>
          <button
            className="up-alert-banner__btn"
            onClick={() => setShowCompleteModal(true)}
          >
            Completa ora
          </button>
        </div>
      )}

      {/* ── Dati personali ── */}
      <Section title="Dati personali" icon="bi-person">
        <form onSubmit={handleSaveProfile} noValidate>
          <div className="up-form-row">
            <div className="up-field">
              <label className="up-label" htmlFor="up-firstName">
                Nome
              </label>
              <input
                id="up-firstName"
                type="text"
                className="up-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
              />
            </div>
            <div className="up-field">
              <label className="up-label" htmlFor="up-lastName">
                Cognome
              </label>
              <input
                id="up-lastName"
                type="text"
                className="up-input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="up-field">
            <label className="up-label" htmlFor="up-email">
              Email
            </label>
            <input
              id="up-email"
              type="email"
              className="up-input up-input--disabled"
              value={user.email}
              readOnly
              aria-describedby="up-email-note"
            />
            <p id="up-email-note" className="up-field-note">
              L'email non può essere modificata da qui.
            </p>
          </div>

          {saveError && (
            <div className="up-inline-error" role="alert">
              {saveError}
            </div>
          )}
          {savedOk && (
            <div className="up-inline-success" role="status">
              <i className="bi bi-check-circle-fill me-1" />
              Profilo aggiornato!
            </div>
          )}

          <button type="submit" className="up-btn-primary" disabled={saving}>
            {saving ? (
              <>
                <span className="up-spinner" aria-hidden="true" /> Salvataggio…
              </>
            ) : (
              <>
                <i className="bi bi-floppy me-2" />
                Salva modifiche
              </>
            )}
          </button>
        </form>
      </Section>

      {/* ── Account free senza password — nessuna password da cambiare ── */}
      {isEmailProvider && user.accountStatus === 'free_only' && (
        <Section title="Sicurezza" icon="bi-shield-lock">
          <p className="up-field-note mb-3">
            Non hai ancora impostato una password: accedi solo tramite il link
            ricevuto via email. Completa la registrazione per impostarne una.
          </p>
          <button
            type="button"
            className="up-btn-primary"
            onClick={() => setShowCompleteModal(true)}
          >
            <i className="bi bi-stars me-2" />
            Completa registrazione
          </button>
        </Section>
      )}

      {/* ── Cambio password — solo per utenti email con password già impostata ── */}
      {isEmailProvider && user.accountStatus !== 'free_only' && (
        <Section title="Sicurezza" icon="bi-shield-lock">
          <form onSubmit={handleChangePassword} noValidate>
            {[
              {
                id: 'up-current-pwd',
                label: 'Password attuale',
                val: currentPwd,
                set: setCurrentPwd,
                show: showCurrent,
                toggle: () => setShowCurrent((v) => !v),
              },
              {
                id: 'up-new-pwd',
                label: 'Nuova password',
                val: newPwd,
                set: setNewPwd,
                show: showNew,
                toggle: () => setShowNew((v) => !v),
              },
              {
                id: 'up-confirm-pwd',
                label: 'Conferma nuova pass',
                val: confirmPwd,
                set: setConfirmPwd,
                show: showConfirm,
                toggle: () => setShowConfirm((v) => !v),
              },
            ].map(({ id, label, val, set, show, toggle }) => (
              <div className="up-field" key={id}>
                <label className="up-label" htmlFor={id}>
                  {label}
                </label>
                <div className="up-input-wrap">
                  <input
                    id={id}
                    type={show ? 'text' : 'password'}
                    className="up-input"
                    value={val}
                    onChange={(e) => set(e.target.value)}
                    autoComplete={
                      id === 'up-current-pwd'
                        ? 'current-password'
                        : 'new-password'
                    }
                  />
                  <button
                    type="button"
                    className="up-input-toggle"
                    onClick={toggle}
                    aria-label="Mostra/nascondi password"
                  >
                    <i className={`bi ${show ? 'bi-eye-slash' : 'bi-eye'}`} />
                  </button>
                </div>
              </div>
            ))}

            {pwdError && (
              <div className="up-inline-error" role="alert">
                {pwdError}
              </div>
            )}
            {pwdOk && (
              <div className="up-inline-success" role="status">
                <i className="bi bi-check-circle-fill me-1" />
                Password aggiornata!
              </div>
            )}

            <button
              type="submit"
              className="up-btn-primary"
              disabled={pwdSaving}
            >
              {pwdSaving ? (
                <>
                  <span className="up-spinner" aria-hidden="true" />{' '}
                  Aggiornamento…
                </>
              ) : (
                <>
                  <i className="bi bi-lock me-2" />
                  Cambia password
                </>
              )}
            </button>
          </form>
          <p className="up-field-note mt-3">
            Almeno 8 caratteri, una maiuscola, un numero e un simbolo.
          </p>
        </Section>
      )}

      {/* ── Social provider ── */}
      {!isEmailProvider && (
        <Section title="Sicurezza" icon="bi-shield-lock">
          <div className="up-social-info">
            <i className="bi bi-info-circle" aria-hidden="true" />
            <p>
              Il tuo account è collegato tramite{' '}
              <strong>{PROVIDER_LABEL[user.authProvider]}</strong>. La password
              viene gestita dal provider.
            </p>
          </div>
        </Section>
      )}

      {/* ── Zona pericolosa ── */}
      <Section title="Zona pericolosa" icon="bi-exclamation-triangle">
        <p className="up-field-note mb-3">
          L'eliminazione è definitiva e rimuove tutti i tuoi dati, progressi e
          accessi ai corsi acquistati.
        </p>
        {deleteError && (
          <div className="up-inline-error mb-3" role="alert">
            {deleteError}
          </div>
        )}
        {confirmDelete && (
          <div className="up-inline-error mb-3" role="alert">
            Sei sicuro? Clicca di nuovo per confermare. Questa azione non può
            essere annullata.
          </div>
        )}
        <button
          className="up-btn-danger"
          type="button"
          onClick={handleDeleteAccount}
          disabled={deletingAccount}
        >
          {deletingAccount ? (
            <>
              <span className="up-spinner" aria-hidden="true" /> Eliminazione…
            </>
          ) : (
            <>
              <i className="bi bi-trash me-2" />
              {confirmDelete ? 'Conferma eliminazione' : 'Elimina account'}
            </>
          )}
        </button>
        {confirmDelete && !deletingAccount && (
          <button
            className="up-btn-secondary ms-2"
            type="button"
            onClick={() => setConfirmDelete(false)}
          >
            Annulla
          </button>
        )}
      </Section>

      {/* ── Modal completamento registrazione free ── */}
      <LoginModal
        show={showCompleteModal}
        onHide={() => setShowCompleteModal(false)}
        disableRedirect
        initialView="register"
        lockEmail={user.email}
        onLoginSuccess={() => setShowCompleteModal(false)}
      />
    </div>
  );
};

export default UserProfile;
